import { useCallback, useEffect, useRef, useState } from 'react';
import ApiDatabase from 'server';

const EMPTY_COUNTS = { pending: 0, validatedByAI: 0 };

/**
 * One page of the licence and CACES lists of the identity validation screen,
 * searched and paginated by the api: GET admin/licences/pending (one row per
 * temp worker) or admin/licences/validated-by-ai (one row per entry). Both
 * answers carry the counts of the two tabs.
 *
 * `refreshCounts()` asks the same page again and only takes its counts and
 * totals: the rows on screen stay, so a card can finish leaving.
 */
const useLicenceValidationList = ({ enabled, tab, page, limit, search }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPendingLicences, setTotalPendingLicences] = useState(0);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [status, setStatus] = useState('loading');
  // Only the latest request of each kind may write: a slow answer for an older
  // search or page must not replace the newer one. A counts refresh keeps its
  // own counter and abort key, so it never cancels a page being loaded.
  const latestRequestRef = useRef({ list: 0, counts: 0 });

  const fetchPage = useCallback(
    ({ countsOnly = false } = {}) => {
      const kind = countsOnly ? 'counts' : 'list';
      const requestId = latestRequestRef.current[kind] + 1;
      latestRequestRef.current[kind] = requestId;
      const isLatest = () => latestRequestRef.current[kind] === requestId;
      if (!countsOnly) setStatus('loading');

      const isValidatedTab = tab === 'validated';
      const getList = isValidatedTab
        ? ApiDatabase.getLicencesValidatedByAI
        : ApiDatabase.getPendingLicences;
      const url = isValidatedTab
        ? 'admin/licences/validated-by-ai'
        : 'admin/licences/pending';

      getList(
        { page, limit, search },
        (response) => {
          if (!isLatest()) return;
          setCounts({ ...EMPTY_COUNTS, ...(response?.counts || {}) });
          setTotal(Number(response?.total) || 0);
          setTotalPendingLicences(Number(response?.totalPendingLicences) || 0);
          if (!countsOnly) {
            setRows(Array.isArray(response?.data) ? response.data : []);
            setStatus('ready');
          }
        },
        (error) => {
          if (error?.name === 'CanceledError' || !isLatest()) return;
          console.error(error);
          if (!countsOnly) setStatus('error');
        },
        { abortKey: countsOnly ? `${url}#counts` : url },
      );
    },
    [tab, page, limit, search],
  );

  useEffect(() => {
    if (enabled) fetchPage();
  }, [enabled, fetchPage]);

  const reload = useCallback(() => fetchPage(), [fetchPage]);
  const refreshCounts = useCallback(
    () => fetchPage({ countsOnly: true }),
    [fetchPage],
  );

  return {
    rows,
    setRows,
    total,
    totalPendingLicences,
    counts,
    isLoading: status === 'loading',
    hasError: status === 'error',
    reload,
    refreshCounts,
  };
};

export default useLicenceValidationList;
